import React, { useCallback, useEffect, useState, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Stack,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Article as ArticleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Add as AddIcon,
  History as HistoryIcon,
  EditNote as EditNoteIcon,
  ContentCopy as ContentCopyIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { articleApi, KnowledgeEntry, WeChatArticleDraftSyncResponse, WeChatArticleHistory } from '../services/api';

type HistorySidebarProps = {
  historyList: WeChatArticleHistory[];
  selectedHistoryId: number | null;
  historyLoading: boolean;
  onNewConversation: () => void;
  onOpenHistory: (item: WeChatArticleHistory) => void;
};

const HistorySidebar = memo(function HistorySidebar({
  historyList,
  selectedHistoryId,
  historyLoading,
  onNewConversation,
  onOpenHistory,
}: HistorySidebarProps) {
  return (
    <Paper sx={{ p: 2, alignSelf: 'start', height: { md: 'calc(100vh - 190px)' }, overflow: 'auto' }}>
      <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon fontSize="small" />
        对话历史
      </Typography>
      <Button fullWidth variant="contained" startIcon={<AddIcon />} sx={{ mb: 1.5 }} onClick={onNewConversation}>
        新增对话
      </Button>
      <Divider sx={{ mb: 1 }} />
      <List dense disablePadding>
        <ListItemButton selected={selectedHistoryId === null} onClick={onNewConversation}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <EditNoteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="新建对话" secondary="当前草稿" primaryTypographyProps={{ noWrap: true }} />
        </ListItemButton>
        {historyList.map((item) => (
          <ListItemButton
            key={item.id}
            selected={selectedHistoryId === item.id}
            onClick={() => onOpenHistory(item)}
            disabled={historyLoading && selectedHistoryId === item.id}
          >
            <ListItemText
              primary={item.topic}
              secondary={new Date(item.createdAt).toLocaleString()}
              primaryTypographyProps={{ noWrap: true }}
            />
          </ListItemButton>
        ))}
      </List>
      {historyList.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          暂无历史记录
        </Typography>
      )}
    </Paper>
  );
});

const TEMPLATES = [
  { value: 'general', label: '通用公众号长文' },
  { value: 'news', label: '新闻通稿' },
  { value: 'event', label: '活动通知' },
  { value: 'policy', label: '政策解读' },
  { value: 'product', label: '产品发布' },
];

type ArticleFormProps = {
  topic: string;
  extra: string;
  template: string;
  loading: boolean;
  error: string | null;
  suggestedTitles: string[];
  onTopicChange: (value: string) => void;
  onExtraChange: (value: string) => void;
  onTemplateChange: (value: string) => void;
  onErrorClose: () => void;
  onGenerate: () => void;
  onSuggestTitles: () => void;
  onSelectTitle: (title: string) => void;
};

const ArticleForm = memo(function ArticleForm({
  topic,
  extra,
  template,
  loading,
  error,
  suggestedTitles,
  onTopicChange,
  onExtraChange,
  onTemplateChange,
  onErrorClose,
  onGenerate,
  onSuggestTitles,
  onSelectTitle,
}: ArticleFormProps) {
  const [titleLoading, setTitleLoading] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);

  const handleSuggest = useCallback(async () => {
    if (!topic.trim()) {
      setTitleError('请填写主题后再获取标题建议');
      return;
    }
    setTitleLoading(true);
    setTitleError(null);
    try {
      await onSuggestTitles();
    } catch (err: any) {
      setTitleError(err?.message || '获取标题失败');
    } finally {
      setTitleLoading(false);
    }
  }, [topic, onSuggestTitles]);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack spacing={2}>
        <FormControl fullWidth>
          <InputLabel id="article-template-label">文章模板</InputLabel>
          <Select
            labelId="article-template-label"
            value={template}
            label="文章模板"
            onChange={(e) => onTemplateChange(e.target.value)}
          >
            {TEMPLATES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="主题 / 核心信息"
          placeholder="例如：介绍我司新上线的知识库能力与使用场景"
          value={topic}
          onChange={(e) => onTopicChange(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          required
        />
        <TextField
          label="额外要求（可选）"
          placeholder="字数、必含要点、不要提及的数据等"
          value={extra}
          onChange={(e) => onExtraChange(e.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
        {suggestedTitles.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              候选标题（点击使用）
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {suggestedTitles.map((title) => (
                <Chip
                  key={title}
                  label={title}
                  onClick={() => onSelectTitle(title)}
                  clickable
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
        {titleError && (
          <Alert severity="error" onClose={() => setTitleError(null)}>
            {titleError}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={onErrorClose}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={onGenerate}
            disabled={loading}
          >
            {loading ? '生成中…' : '根据知识库生成草稿'}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={handleSuggest}
            disabled={loading || titleLoading}
          >
            {titleLoading ? '标题生成中…' : 'AI 推荐标题'}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
});

type ArticlePreviewProps = {
  article: string;
  sources: KnowledgeEntry[];
};

const ArticlePreview = memo(function ArticlePreview({ article, sources }: ArticlePreviewProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyDraft = useCallback(async () => {
    if (!article) {
      return;
    }
    try {
      await navigator.clipboard.writeText(article);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = article;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  }, [article]);

  if (!article && sources.length === 0) {
    return null;
  }

  return (
    <>
      {sources.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            参考知识片段
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {sources.map((s) => (
              <Chip
                key={s.id}
                size="small"
                label={s.document?.title ? `${s.document.title} (#${s.id})` : `条目 #${s.id}`}
              />
            ))}
          </Box>
        </Paper>
      )}
      {article && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
            <Typography variant="h6">草稿预览</Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyDraft}
            >
              {copied ? '已复制' : '复制草稿'}
            </Button>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box
            className="wechat-article-md"
            sx={{
              '& h1, & h2, & h3': { mt: 2, mb: 1 },
              '& p': { mb: 1.5, lineHeight: 1.75 },
              '& ul': { pl: 2, mb: 1.5 },
            }}
          >
            <ReactMarkdown>{article}</ReactMarkdown>
          </Box>
        </Paper>
      )}
    </>
  );
});

type DraftSyncPanelProps = {
  historyId: number;
  defaultTitle: string;
  draftStatus?: string;
  draftSyncedAt?: string;
  draftError?: string;
  onSynced: (result: WeChatArticleDraftSyncResponse) => void;
};

const DraftSyncPanel = memo(function DraftSyncPanel({
  historyId,
  defaultTitle,
  draftStatus,
  draftSyncedAt,
  draftError,
  onSynced,
}: DraftSyncPanelProps) {
  const [title, setTitle] = useState(defaultTitle);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTitle(defaultTitle);
  }, [defaultTitle, historyId]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setError(null);
    setMessage(null);
    try {
      const formData = new FormData();
      if (title.trim()) {
        formData.append('title', title.trim());
      }
      if (coverFile) {
        formData.append('coverImage', coverFile);
      }
      const result = await articleApi.syncDraftToWeChat(historyId, formData);
      setMessage(result.message);
      onSynced(result);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '同步失败';
      setError(typeof msg === 'string' ? msg : '同步失败');
    } finally {
      setSyncing(false);
    }
  }, [historyId, title, coverFile, onSynced]);

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudUploadIcon color="primary" fontSize="small" />
        同步到公众号草稿箱
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        将 Markdown 转为图文草稿并写入微信公众平台草稿箱；请在 mp.weixin.qq.com 审校后手动发布。
      </Typography>
      {draftStatus === 'SYNCED' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          已同步{draftSyncedAt ? `（${new Date(draftSyncedAt).toLocaleString()}）` : ''}，请登录公众平台草稿箱继续编辑发布。
        </Alert>
      )}
      {draftStatus === 'FAILED' && draftError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          上次同步失败：{draftError}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Stack spacing={2}>
        <TextField
          label="文章标题（同步到微信，最多 64 字）"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
        />
        <Box>
          <Button variant="outlined" component="label" size="small">
            {coverFile ? `封面：${coverFile.name}` : '选择封面图（JPG/PNG，建议 900×500）'}
            <input
              type="file"
              hidden
              accept="image/jpeg,image/png,image/jpg"
              onChange={(e) => setCoverFile(e.target.files?.[0] ?? null)}
            />
          </Button>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            同步前请上传封面图，或在服务端配置 WECHAT_MP_DEFAULT_COVER_MEDIA_ID；并开启 WECHAT_MP_ENABLED=true。
          </Typography>
        </Box>
        <Box>
          <Button
            variant="contained"
            startIcon={syncing ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? '同步中…' : '同步到草稿箱'}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
});

const WeChatArticle: React.FC = () => {
  const [topic, setTopic] = useState('');
  const [extra, setExtra] = useState('');
  const [template, setTemplate] = useState('general');
  const [suggestedTitles, setSuggestedTitles] = useState<string[]>([]);
  const [article, setArticle] = useState('');
  const [sources, setSources] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyList, setHistoryList] = useState<WeChatArticleHistory[]>([]);
  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null);
  const [draftMeta, setDraftMeta] = useState<{
    status?: string;
    syncedAt?: string;
    error?: string;
  }>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await articleApi.getHistoryList();
        if (!cancelled) {
          setHistoryList(list || []);
        }
      } catch (e) {
        console.error('加载历史记录失败', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const list = await articleApi.getHistoryList();
      setHistoryList(list || []);
    } catch (e) {
      console.error('加载历史记录失败', e);
    }
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!topic.trim()) {
      setError('请填写文章主题或要点');
      return;
    }
    setLoading(true);
    setError(null);
    setArticle('');
    setSources([]);
    try {
      const data = await articleApi.generateWeChatArticle({
        topic: topic.trim(),
        extraInstructions: extra.trim() || undefined,
        maxResults: 8,
        template: template || 'general',
        baseHistoryId: selectedHistoryId ?? undefined,
      });
      setArticle(data.article || '');
      setSources(data.sourcesUsed || []);
      setSelectedHistoryId(data.historyId ?? null);
      setDraftMeta({});
      await loadHistory();
    } catch (err: any) {
      const d = err?.response?.data;
      const msg =
        (typeof d === 'object' && d?.message) ||
        (typeof d === 'string' ? d : null) ||
        err?.message ||
        '生成失败，请稍后重试';
      setError(typeof msg === 'string' ? msg : '生成失败');
    } finally {
      setLoading(false);
    }
  }, [topic, extra, template, selectedHistoryId, loadHistory]);

  const handleSuggestTitles = useCallback(async () => {
    const data = await articleApi.suggestTitles({
      topic: topic.trim(),
    });
    setSuggestedTitles(data.titles || []);
  }, [topic]);

  const handleSelectTitle = useCallback((title: string) => {
    setTopic(title);
  }, []);

  const handleOpenHistory = useCallback(async (item: WeChatArticleHistory) => {
    setSelectedHistoryId(item.id);
    setTopic(item.topic || '');
    setExtra(item.extraInstructions || '');
    setTemplate(item.template || 'general');
    setSuggestedTitles([]);
    setHistoryLoading(true);
    try {
      const detail = await articleApi.getHistoryDetail(item.id);
      setArticle(detail.article || '');
      setSources(detail.sourcesUsed || []);
      setDraftMeta({
        status: item.wechatDraftStatus,
        syncedAt: item.wechatDraftSyncedAt,
        error: item.wechatDraftError,
      });
      setError(null);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || '加载历史详情失败';
      setError(msg);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const handleNewConversation = useCallback(() => {
    setSelectedHistoryId(null);
    setTopic('');
    setExtra('');
    setTemplate('general');
    setSuggestedTitles([]);
    setArticle('');
    setSources([]);
    setDraftMeta({});
    setError(null);
  }, []);

  const handleDraftSynced = useCallback(
    (result: WeChatArticleDraftSyncResponse) => {
      setDraftMeta({
        status: result.draftStatus,
        syncedAt: result.syncedAt,
        error: undefined,
      });
      setHistoryList((prev) =>
        prev.map((item) =>
          item.id === result.historyId
            ? {
                ...item,
                wechatDraftStatus: result.draftStatus,
                wechatDraftTitle: result.title,
                wechatDraftMediaId: result.mediaId,
                wechatDraftSyncedAt: result.syncedAt,
                wechatDraftError: undefined,
              }
            : item
        )
      );
    },
    []
  );

  const activeConversationTitle =
    selectedHistoryId != null
      ? historyList.find((item) => item.id === selectedHistoryId)?.topic || '历史会话'
      : '新建对话';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '280px 1fr' },
        gap: 2,
        minHeight: { md: 'calc(100vh - 180px)' },
      }}
    >
      <HistorySidebar
        historyList={historyList}
        selectedHistoryId={selectedHistoryId}
        historyLoading={historyLoading}
        onNewConversation={handleNewConversation}
        onOpenHistory={handleOpenHistory}
      />

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArticleIcon color="primary" />
          公众号文章生成
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          根据企业知识库全库自动检索并生成草稿，语气与读者画像由系统默认设定。
        </Typography>
        <Chip label={`当前会话：${activeConversationTitle}`} sx={{ mb: 2 }} />

        <ArticleForm
          topic={topic}
          extra={extra}
          template={template}
          loading={loading}
          error={error}
          suggestedTitles={suggestedTitles}
          onTopicChange={setTopic}
          onExtraChange={setExtra}
          onTemplateChange={setTemplate}
          onErrorClose={() => setError(null)}
          onGenerate={handleGenerate}
          onSuggestTitles={handleSuggestTitles}
          onSelectTitle={handleSelectTitle}
        />

        {historyLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!historyLoading && <ArticlePreview article={article} sources={sources} />}

        {!historyLoading && selectedHistoryId != null && article && (
          <DraftSyncPanel
            historyId={selectedHistoryId}
            defaultTitle={topic.trim() || '未命名文章'}
            draftStatus={draftMeta.status}
            draftSyncedAt={draftMeta.syncedAt}
            draftError={draftMeta.error}
            onSynced={handleDraftSynced}
          />
        )}
      </Box>
    </Box>
  );
};

export default WeChatArticle;
